import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Upload,
  FileType,
  Car,
  Cpu,
  Loader2,
  AlertCircle,
  X,
  Wrench,
  CheckCircle,
  Lock,
  ShoppingCart,
  Download,
  Zap,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const READING_TOOLS = [
  { value: 'flex', label: 'Flex' },
  { value: 'kess', label: 'Kess' },
  { value: 'dfox', label: 'Dfox' },
  { value: 'mpps', label: 'MPPS' },
  { value: 'ktag', label: 'KTag' },
  { value: 'pcmflash', label: 'PCMFlash' },
  { value: 'trasdata', label: 'Trasdata' },
  { value: 'other', label: 'Otra' },
];

const EXCLUSIVE_SERVICES = ['stage1', 'stage2', 'eco'];

const DIESEL_ONLY_SERVICES = ['dpf'];
const GASOLINE_ONLY_SERVICES = ['pop_bang'];

const INDUSTRIAL_ONLY_SERVICES = ['eco', 'stage1', 'dpf', 'egr', 'adblue'];

const GASOLINE_PRICES = {
  stage1: 130.0,
  stage2: 180.0,
};

const INDUSTRIAL_PRICES = {
  dpf: 400.0,
  egr: 400.0,
  adblue: 400.0,
  stage1: 400.0,
};

const EMISSION_SERVICES = ['dpf', 'egr', 'adblue'];

// Known ECU types for autocomplete
const ECU_LIST = [
  // Bosch EDC16
  "EDC16C0","EDC16C2","EDC16C3","EDC16C4","EDC16C7","EDC16C8","EDC16C9","EDC16C10","EDC16C11","EDC16C14",
  "EDC16C31","EDC16C32","EDC16C34","EDC16C35","EDC16C36","EDC16C39","EDC16C41","EDC16C46",
  "EDC16CP31","EDC16CP33","EDC16U1","EDC16U3","EDC16U31","EDC16U34",
  // Bosch EDC17
  "EDC17C06","EDC17C08","EDC17C10","EDC17C11","EDC17C14","EDC17C41","EDC17C42","EDC17C43","EDC17C44",
  "EDC17C45","EDC17C46","EDC17C47","EDC17C49","EDC17C50","EDC17C53","EDC17C54","EDC17C56","EDC17C57",
  "EDC17C60","EDC17C64","EDC17C69","EDC17C74","EDC17C79","EDC17C83","EDC17C84",
  "EDC17CP04","EDC17CP05","EDC17CP09","EDC17CP14","EDC17CP20","EDC17CP24","EDC17CP44","EDC17CP45",
  "EDC17CP46","EDC17CP50","EDC17CP52","EDC17CP54","EDC17CP57",
  // Bosch MED/ME/MG/MD
  "MED17.1","MED17.1.1","MED17.1.6","MED17.1.27","MED17.5","MED17.5.1","MED17.5.2","MED17.5.5",
  "MED17.5.6","MED17.5.20","MED17.5.21","MED17.5.25",
  "ME7.5","ME7.8","ME7.1.1","ME17.5","ME17.5.6","ME17.5.20",
  "MEVD17.2","MEVD17.2.4","MEVD17.2.6","MEVD17.2.8","MEVD17.2.9","MEVD17.3.5",
  "MG1CS002","MG1CS003","MG1CS011","MG1CS024","MG1CS042","MG1CA002","MG1CA007",
  "MD1CS001","MD1CS002","MD1CS003","MD1CS004","MD1CS006","MD1CP002","MD1CP004","MD1CE100","MD1CE101",
  // Continental/Siemens
  "SID201","SID202","SID206","SID208","SID209","SID210","SID301","SID302","SID303","SID305","SID310",
  "SID803","SID803A","SID804","SID807",
  "EMS3110","EMS3125","EMS3130","EMS3132","EMS3134","EMS3150",
  "SIMOS18.1","SIMOS18.2","SIMOS18.6","SIMOS18.10","SIMOS12.1","SIMOS12.2",
  "SIMOS PCR2.1","PCR2.1","SIMOS19.1","SIMOS19.3","SIMOS19.6",
  // Delphi
  "DCM3.4","DCM3.5","DCM3.7","DCM6.1","DCM6.2A","DCM6.2C","DCM7.1A","DCM7.1B",
  "CRD2","CRD3","CRD3.10","CRD3.30","CRD3.40","CRD3.60","DDCR","DDCRP2",
  // Denso
  "SH7058","SH7055","SH7059","SH72531","SH72543","DENSO",
  // Marelli
  "IAW5SF","IAW5AM","IAW5NF","IAW59F","IAW4AF","IAW4CF","IAW4GF","IAW4MP","IAW7GF","IAW6LP",
  "MJD6F3","MJD6JX","MJD8F2","MJD8DF","MJD9DF",
  // ZF/Temic TCU
  "DQ200","DQ250","DQ381","DQ500","ZF6HP","ZF8HP","AL551","AL950",
  // Other
  "ACDelco E38","ACDelco E67","ACDelco E78",
  // TRW
  "TRW EMS2.3","TRW EMS2.4","TRW DCT","TRW 2.4 DCT",
  // Visteon
  "DCU102","DCU104","DCU106",
  // Hitachi
  "Hitachi GEN3","Hitachi GEN4",
].sort();

const NewRequestPage = () => {
  const { user, token, refreshUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);

  // Vehicle
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [vin, setVin] = useState('');
  const [transmission, setTransmission] = useState('');
  const [ecuType, setEcuType] = useState('');
  const [showEcuSuggestions, setShowEcuSuggestions] = useState(false);
  const [fuelType, setFuelType] = useState('diesel');
  const [vehicleType, setVehicleType] = useState('turismo');

  // Request
  const [unitType, setUnitType] = useState('ecu');
  const [readingTool, setReadingTool] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);
  const [notes, setNotes] = useState('');
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // File
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [fileAnalysis, setFileAnalysis] = useState(null);
  const [fileSource, setFileSource] = useState('master'); // 'master' or 'slave'
  const [slaveTools, setSlaveTools] = useState([]);
  const [selectedSlaveTool, setSelectedSlaveTool] = useState('');
  const [memoryType, setMemoryType] = useState('int_flash');
  const [decrypting, setDecrypting] = useState(false);
  const [decryptedFile, setDecryptedFile] = useState(null); // {job_id, status, filename}
  const [quickBuying, setQuickBuying] = useState(false);
  const [quickBuyResult, setQuickBuyResult] = useState(null);
  const [queueInfo, setQueueInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/services`).then(r => setServices(r.data.services || [])).catch(() => {});
    axios.get(`${API}/mms/user-tools`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const tools = r.data.tools || [];
        setSlaveTools(tools);
        if (tools.length > 0 && !selectedSlaveTool) {
          setSelectedSlaveTool(tools[0].machine_sn || tools[0].serial_number || tools[0].sn);
        }
      }).catch(() => {});
  }, [token]);

  const getServicePrice = (service) => {
    if (vehicleType === 'industrial' && service.industrial_price) {
      return service.industrial_price;
    }
    if (vehicleType === 'industrial' && INDUSTRIAL_PRICES[service.id]) {
      return INDUSTRIAL_PRICES[service.id];
    }
    if (fuelType === 'gasolina' && GASOLINE_PRICES[service.id]) {
      return GASOLINE_PRICES[service.id];
    }
    return service.price;
  };

  // Filter services based on fuel type, vehicle type and deselect hidden ones
  const visibleServices = services.filter(s => {
    if (s.active === false) return false;
    if (vehicleType === 'industrial') {
      if (!INDUSTRIAL_ONLY_SERVICES.includes(s.id)) return false;
      if (s.active_industrial === false) return false;
    } else {
      if (s.active_turismo === false) return false;
    }
    if (fuelType === 'gasolina' && DIESEL_ONLY_SERVICES.includes(s.id)) return false;
    if (fuelType === 'diesel' && GASOLINE_ONLY_SERVICES.includes(s.id)) return false;
    return true;
  });

  useEffect(() => {
    setSelectedServices(prev => prev.filter(id => {
      if (vehicleType === 'industrial' && !INDUSTRIAL_ONLY_SERVICES.includes(id)) return false;
      if (fuelType === 'gasolina' && DIESEL_ONLY_SERVICES.includes(id)) return false;
      if (fuelType === 'diesel' && GASOLINE_ONLY_SERVICES.includes(id)) return false;
      return true;
    }));
  }, [fuelType, vehicleType]);

  // Auto-analyze .bin file when selected (Master mode)
  // or auto-decrypt + analyze (Slave mode — free, no MMS credits charged)
  useEffect(() => {
    if (!file || !user) return;

    if (fileSource === 'slave') {
      // Auto-detect memory type from the .mmf filename
      const n = (file.name || '').toLowerCase();
      let mem = memoryType;
      if (n.includes('int_flash')) mem = 'int_flash';
      else if (n.includes('ext_flash')) mem = 'ext_flash';
      else if (n.includes('int_eeprom')) mem = 'int_eeprom';
      else if (n.includes('ext_eeprom')) mem = 'ext_eeprom';
      else if (n.includes('eeprom')) mem = 'int_eeprom';
      if (mem !== memoryType) setMemoryType(mem);
      // Slave mode: create a FLEX decrypt job with source=ticket (free)
      if (!selectedSlaveTool) return;
      const decryptFile = async () => {
        setDecrypting(true);
        setFileAnalysis(null);
        setDecryptedFile(null);
        try {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('machine_sn', selectedSlaveTool);
          fd.append('vehicle_id', licensePlate || 'AUTO');
          fd.append('source', 'ticket');
          fd.append('memory_type', mem);
          const res = await axios.post(`${API}/jobs`, fd, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          });
          setDecryptedFile({ job_id: res.data.job_id, status: res.data.status });
          if (res.data.deduplicated) {
            setDecrypting(false);
            toast.success('Archivo ya procesado anteriormente');
            return;
          }
          toast.info('Desencriptando archivo SLAVE...');
          pollJobStatus(res.data.job_id);
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Error al desencriptar');
          setDecrypting(false);
        }
      };
      decryptFile();
      return;
    }

    // Master mode: analyze any uploaded file
    const analyzeFile = async () => {
      setAnalyzing(true);
      setFileAnalysis(null);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/check-file`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setFileAnalysis(res.data);
        if (res.data.match_found) toast.success(res.data.message || 'Solucion encontrada!');
        else if (res.data.validation_error) toast.warning(res.data.message);
        else if (res.data.analysis || res.data.firmware_info) toast.info('Archivo analizado');
      } catch (e) {
        console.error('File analysis error:', e);
      }
      finally { setAnalyzing(false); }
    };
    analyzeFile();
  }, [file, user, fileSource, selectedSlaveTool]);

  const pollJobStatus = async (jobId) => {
    const poll = async () => {
      try {
        const [jobRes, queueRes] = await Promise.all([
          axios.get(`${API}/jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/mms/queue-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        ]);
        const job = jobRes.data.job;
        setDecryptedFile({ job_id: jobId, status: job.status, filename: job.extracted_bin_filename });
        if (queueRes?.data) setQueueInfo(queueRes.data);

        if (['MATCH_FOUND', 'MATCH_NOT_FOUND'].includes(job.status)) {
          setDecrypting(false); setQueueInfo(null);
          // Build fileAnalysis from job data (same format as check-file)
          setFileAnalysis({
            match_found: job.match_found || job.status === 'MATCH_FOUND',
            solution_id: job.solution_id,
            vehicle: job.vehicle || job.match_vehicle || '',
            ecu_type: job.ecu_type || job.match_ecu || '',
            engine_code: job.engine_code || '',
            horsepower: job.horsepower || '',
            match_type: job.match_type || '',
            available_solutions: job.available_solutions || [],
            analysis: job.analysis,
          });
          if (job.status === 'MATCH_FOUND') toast.success('Archivo desencriptado y solucion encontrada!');
          else toast.info('Archivo desencriptado. Sin solucion automatica.');
          return;
        }
        if (job.status === 'FAILED') {
          setDecrypting(false); setQueueInfo(null);
          toast.error(job.error || 'Error al desencriptar');
          return;
        }
        setTimeout(poll, 3000);
      } catch {
        setDecrypting(false);
      }
    };
    setTimeout(poll, 3000);
  };

  const handleQuickBuy = async (versionId) => {
    setQuickBuying(true);
    try {
      const payload = {
        solution_id: fileAnalysis.solution_id,
        version_id: versionId,
      };
      if (fileSource === 'slave' && selectedSlaveTool) {
        payload.machine_sn = selectedSlaveTool;
      }
      if (decryptedFile?.job_id) {
        payload.flex_job_id = decryptedFile.job_id;
      }
      const res = await axios.post(`${API}/quick-buy`, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setQuickBuyResult(res.data);
      toast.success(res.data.message);
      refreshUser();
      // Auto-download via blob to avoid popup blocker and cross-origin issues
      try {
        const dlRes = await axios.get(`${API}/downloads/${res.data.download_token}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(dlRes.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = res.data.filename || 'archivo';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch {
        // If auto-download fails, user can still use the button
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en la compra');
    } finally {
      setQuickBuying(false);
    }
  };


  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!brand || !model || !ecuType) return toast.error(t('Completa los datos del vehículo'));
    if (!readingTool) return toast.error(t('Selecciona la herramienta de lectura'));
    if (selectedServices.length === 0) return toast.error(t('Selecciona al menos un servicio'));
    if (!file) return toast.error(t('Sube el archivo .bin original'));
    const hasEmission = selectedServices.some(id => EMISSION_SERVICES.includes(id));
    if (hasEmission && !disclaimerAccepted) return toast.error(t('Debes aceptar el aviso legal para servicios de emisiones'));

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('vehicle_brand', brand);
      formData.append('vehicle_model', model);
      formData.append('vehicle_year', year);
      formData.append('license_plate', licensePlate);
      if (vin) formData.append('vin', vin);
      formData.append('transmission', transmission);
      formData.append('ecu_type', ecuType);
      formData.append('fuel_type', fuelType);
      formData.append('vehicle_type', vehicleType);
      formData.append('unit_type', unitType);
      formData.append('reading_tool', readingTool);
      formData.append('service_type', selectedServices.join(','));
      formData.append('notes', notes || '');
      formData.append('reading_type', fileSource === 'slave' ? 'slave' : 'master');
      if (fileSource === 'slave' && selectedSlaveTool) {
        formData.append('slave_machine_sn', selectedSlaveTool);
      }
      if (fileSource === 'slave' && decryptedFile?.job_id) {
        formData.append('flex_job_id', decryptedFile.job_id);
      }
      formData.append('file', file);

      const res = await axios.post(`${API}/tickets`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      await refreshUser();

      // If auto-match found, redirect to ticket with match info
      if (res.data.auto_match) {
        toast.success(t('Solicitud enviada - Se ha encontrado una solucion automatica!'));
      } else {
        toast.success(t('Solicitud enviada correctamente'));
      }
      // Google Ads: track first ticket conversion
      import('../utils/googleAds.js').then(m => m.trackFirstTicket()).catch(() => {});

      navigate(`/tickets/${res.data.ticket_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Error al crear solicitud'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="new-request-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">{t('NUEVA SOLICITUD')}</h1>
          <p className="text-zinc-400 mt-1">{t('Completa los datos del vehículo, sube el archivo y envía la solicitud')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Vehicle Data */}
          <div className="bg-surface border border-white/10 rounded-md p-6">
            <div className="flex items-center gap-3 mb-5">
              <Car className="w-5 h-5 text-primary" />
              <h2 className="font-heading text-lg font-bold text-white">{t('DATOS DEL VEHÍCULO')}</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('Marca')}</Label>
                <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="BMW, Audi, VW..."
                  required className="bg-surface-highlight border-white/10 text-white" data-testid="brand-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('Modelo')}</Label>
                <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="320d, A4, Golf..."
                  required className="bg-surface-highlight border-white/10 text-white" data-testid="model-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('Año')} <span className="text-zinc-600 text-xs">({t('opcional')})</span></Label>
                <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="2020"
                  className="bg-surface-highlight border-white/10 text-white" data-testid="year-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('Tipo ECU')} <span className="text-red-400">*</span></Label>
                <div className="relative">
                  <Input value={ecuType} onChange={(e) => { setEcuType(e.target.value.toUpperCase()); setShowEcuSuggestions(true); }}
                    placeholder="Ej: EDC17C46, MD1CS006, SIMOS18..."
                    required className="bg-surface-highlight border-white/10 text-white" data-testid="ecu-input"
                    onFocus={() => setShowEcuSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowEcuSuggestions(false), 200)}
                    autoComplete="off"
                  />
                  {showEcuSuggestions && ecuType.length >= 2 && (() => {
                    const q = ecuType.toUpperCase().replace(/[\s.\-_/]/g, '');
                    const matches = ECU_LIST.filter(e => e.replace(/[\s.\-_/]/g, '').includes(q)).slice(0, 8);
                    if (matches.length === 0) return null;
                    return (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-surface border border-white/20 rounded-md shadow-xl max-h-48 overflow-y-auto">
                        {matches.map((ecu, i) => (
                          <button key={i} type="button"
                            onMouseDown={(e) => { e.preventDefault(); setEcuType(ecu); setShowEcuSuggestions(false); }}
                            className="w-full text-left px-3 py-2 text-sm text-white hover:bg-primary/20 font-mono">
                            {ecu}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('Matrícula')} <span className="text-red-400">*</span></Label>
                <Input value={licensePlate} onChange={(e) => setLicensePlate(e.target.value.toUpperCase())} placeholder="1234 ABC"
                  required className="bg-surface-highlight border-white/10 text-white" data-testid="plate-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">VIN <span className="text-zinc-600 text-xs">({t('opcional')})</span></Label>
                <Input value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())} placeholder="WBAPH5C55BA..."
                  className="bg-surface-highlight border-white/10 text-white" data-testid="vin-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('Transmisión')} <span className="text-red-400">*</span></Label>
                <Select value={transmission} onValueChange={setTransmission}>
                  <SelectTrigger className="bg-surface-highlight border-white/10 text-white h-12" data-testid="transmission-select">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10">
                    <SelectItem value="manual" className="text-white hover:bg-white/10">{t('Manual')}</SelectItem>
                    <SelectItem value="automatic" className="text-white hover:bg-white/10">{t('Automático')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('Combustible')} <span className="text-red-400">*</span></Label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setFuelType('diesel')}
                    className={`flex-1 py-3 rounded-md border text-sm font-medium transition-all ${fuelType === 'diesel' ? 'bg-primary/10 border-primary text-primary' : 'bg-surface-highlight border-white/10 text-zinc-400 hover:text-white'}`}>
                    Diésel
                  </button>
                  <button type="button" onClick={() => setFuelType('gasolina')}
                    className={`flex-1 py-3 rounded-md border text-sm font-medium transition-all ${fuelType === 'gasolina' ? 'bg-primary/10 border-primary text-primary' : 'bg-surface-highlight border-white/10 text-zinc-400 hover:text-white'}`}>
                    Gasolina
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('Tipo Vehículo')} <span className="text-red-400">*</span></Label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setVehicleType('turismo')} data-testid="vehicle-type-turismo"
                    className={`flex-1 py-3 rounded-md border text-sm font-medium transition-all ${vehicleType === 'turismo' ? 'bg-primary/10 border-primary text-primary' : 'bg-surface-highlight border-white/10 text-zinc-400 hover:text-white'}`}>
                    Turismo
                  </button>
                  <button type="button" onClick={() => setVehicleType('industrial')} data-testid="vehicle-type-industrial"
                    className={`flex-1 py-3 rounded-md border text-sm font-medium transition-all ${vehicleType === 'industrial' ? 'bg-orange-500/10 border-orange-500 text-orange-400' : 'bg-surface-highlight border-white/10 text-zinc-400 hover:text-white'}`}>
                    Industrial
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Reading Info */}
          <div className="bg-surface border border-white/10 rounded-md p-6">
            <div className="flex items-center gap-3 mb-5">
              <Wrench className="w-5 h-5 text-primary" />
              <h2 className="font-heading text-lg font-bold text-white">{t('DATOS DE LECTURA')}</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('Tipo de Unidad')}</Label>
                <Select value={unitType} onValueChange={setUnitType}>
                  <SelectTrigger className="bg-surface-highlight border-white/10 text-white h-12" data-testid="unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10">
                    <SelectItem value="ecu" className="text-white hover:bg-white/10">ECU (Motor)</SelectItem>
                    <SelectItem value="tcu" className="text-white hover:bg-white/10">TCU (Caja de Cambios)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('Herramienta de Lectura')}</Label>
                <Select value={readingTool} onValueChange={setReadingTool}>
                  <SelectTrigger className="bg-surface-highlight border-white/10 text-white h-12" data-testid="tool-select">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10">
                    {READING_TOOLS.map((rt) => (
                      <SelectItem key={rt.value} value={rt.value} className="text-white hover:bg-white/10">{rt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Service */}
          <div className="bg-surface border border-white/10 rounded-md p-6">
            <div className="flex items-center gap-3 mb-2">
              <Cpu className="w-5 h-5 text-primary" />
              <h2 className="font-heading text-lg font-bold text-white">{t('SERVICIOS')}</h2>
            </div>
            <p className="text-zinc-500 text-sm mb-4">{t('Selecciona uno o más servicios.')} <span className="text-yellow-400">{t('Stage 1, Stage 2 y Eco Tuning son excluyentes entre sí.')}</span> {fuelType === 'gasolina' && <span className="text-orange-400">{t('Precios gasolina aplicados a Stage 1/2.')}</span>} {vehicleType === 'industrial' && <span className="text-orange-400">{t('Precios industriales aplicados.')}</span>} {t('Se cobra el precio del servicio más caro.')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="service-checkboxes">
              {visibleServices.map((s) => {
                const checked = selectedServices.includes(s.id);
                const isExclusive = EXCLUSIVE_SERVICES.includes(s.id);
                const otherExclusiveSelected = isExclusive && !checked && selectedServices.some(id => EXCLUSIVE_SERVICES.includes(id) && id !== s.id);
                const displayPrice = getServicePrice(s);
                const hasGasOverride = fuelType === 'gasolina' && GASOLINE_PRICES[s.id];
                const hasIndustrialOverride = vehicleType === 'industrial' && INDUSTRIAL_PRICES[s.id];
                return (
                  <label key={s.id}
                    className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${otherExclusiveSelected ? 'opacity-40 cursor-not-allowed' : ''} ${checked ? 'bg-primary/10 border-primary/40' : 'bg-surface-highlight border-white/10 hover:border-white/20'}`}>
                    <div className={`w-5 h-5 rounded flex items-center justify-center border-2 flex-shrink-0 transition-colors ${checked ? 'bg-primary border-primary' : 'border-zinc-600'}`}>
                      {checked && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                    </div>
                    <input type="checkbox" className="hidden" checked={checked} disabled={otherExclusiveSelected}
                      onChange={() => {
                        if (otherExclusiveSelected) return;
                        setSelectedServices(prev => checked ? prev.filter(id => id !== s.id) : [...prev, s.id]);
                      }} />
                    <span className="text-white text-sm flex-1">{s.name}</span>
                    <span className={`font-mono font-bold text-sm ${hasIndustrialOverride ? 'text-orange-400' : hasGasOverride ? 'text-orange-400' : 'text-primary'}`}>{displayPrice}€</span>
                  </label>
                );
              })}
            </div>
            {selectedServices.length > 0 && (
              <div className="bg-surface-highlight rounded-md p-4 mt-4 flex items-center justify-between">
                <div>
                  <span className="text-zinc-400 text-sm">Seleccionados: </span>
                  <span className="text-white text-sm">{selectedServices.map(id => services.find(s => s.id === id)?.name).join(', ')}</span>
                </div>
                <div>
                  <span className="text-zinc-400 text-sm">Precio: </span>
                  <span className="font-mono text-2xl font-bold text-primary">
                    {Math.max(...selectedServices.map(id => { const s = services.find(sv => sv.id === id); return s ? getServicePrice(s) : 0; }))}€
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Legal Disclaimer for emission services */}
          {selectedServices.some(id => EMISSION_SERVICES.includes(id)) && (
            <div className="bg-amber-500/5 border border-amber-500/30 rounded-md p-5" data-testid="emission-disclaimer">
              <div className="flex items-start gap-3 mb-3">
                <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-heading text-amber-400 font-bold text-sm mb-2">AVISO DE USO LIMITADO</h3>
                  <p className="text-zinc-300 text-sm leading-relaxed mb-2">
                    Este servicio de gestión ({selectedServices.filter(id => EMISSION_SERVICES.includes(id)).map(id => services.find(s => s.id === id)?.name || id).join(', ')}) se suministra <strong className="text-white">exclusivamente para uso en competición, circuitos cerrados o exportación</strong> a países fuera de la Unión Europea / EE.UU. donde la normativa lo permita.
                  </p>
                  <p className="text-zinc-400 text-xs mb-2">
                    <strong className="text-zinc-300">Responsabilidad:</strong> El usuario asume toda la responsabilidad legal derivada del uso de este archivo en vías públicas.
                  </p>
                  <p className="text-zinc-400 text-xs mb-3">
                    <strong className="text-zinc-300">Cumplimiento:</strong> AR Performance no promueve el incumplimiento de las normativas de emisiones vigentes. Al continuar, usted confirma que conoce la legislación de su país y que el vehículo no circulará por vías públicas.
                  </p>
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer pl-8">
                <div className={`w-5 h-5 rounded flex items-center justify-center border-2 flex-shrink-0 transition-colors ${disclaimerAccepted ? 'bg-amber-500 border-amber-500' : 'border-zinc-600'}`}>
                  {disclaimerAccepted && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                </div>
                <input type="checkbox" className="hidden" checked={disclaimerAccepted} onChange={(e) => setDisclaimerAccepted(e.target.checked)} data-testid="disclaimer-checkbox" />
                <span className="text-amber-300 text-sm font-medium">{t('Entiendo y acepto los términos de uso')}</span>
              </label>
            </div>
          )}

          {/* File Upload */}
          <div className="bg-surface border border-white/10 rounded-md p-6">
            <div className="flex items-center gap-3 mb-3">
              <Upload className="w-5 h-5 text-primary" />
              <h2 className="font-heading text-lg font-bold text-white">{t('ARCHIVO ORIGINAL')}</h2>
            </div>

            {/* Master/Slave toggle */}
            <div className="flex gap-2 mb-4">
              <button type="button" onClick={() => { setFileSource('master'); setFile(null); setDecryptedFile(null); setFileAnalysis(null); }}
                className={`flex-1 py-2.5 rounded-md border text-sm font-medium transition-all ${fileSource === 'master' ? 'bg-primary/10 border-primary text-primary' : 'bg-surface-highlight border-white/10 text-zinc-400 hover:text-white'}`}>
                Master (sin encriptar)
              </button>
              <button type="button" onClick={() => { setFileSource('slave'); setFile(null); setDecryptedFile(null); setFileAnalysis(null); }}
                className={`flex-1 py-2.5 rounded-md border text-sm font-medium transition-all ${fileSource === 'slave' ? 'bg-blue-500/10 border-blue-500 text-blue-400' : 'bg-surface-highlight border-white/10 text-zinc-400 hover:text-white'}`}>
                SLAVE (encriptado FLEX)
              </button>
            </div>

            {fileSource === 'master' && (
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-md p-3 mb-4 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                <p className="text-yellow-400 text-xs">
                  Sube el archivo .bin sin encriptar leído con herramienta Master.
                </p>
              </div>
            )}

            {fileSource === 'slave' && (
              <>
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-md p-3 mb-4 flex items-start gap-2">
                  <Cpu className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-blue-300 text-xs">
                    Sube el archivo encriptado de tu herramienta SLAVE. Se desencriptará automáticamente y se buscarán soluciones. Al admin le llegarán ambos archivos (.mmf y .bin).
                  </p>
                </div>
                <div className="mb-4">
                  <Label className="text-zinc-300 text-sm mb-1 block">Herramienta SLAVE <span className="text-red-400">*</span></Label>
                  <Select value={selectedSlaveTool} onValueChange={setSelectedSlaveTool}>
                    <SelectTrigger className="bg-surface-highlight border-white/10 text-white h-11">
                      <SelectValue placeholder="Seleccionar herramienta SLAVE..." />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-white/10">
                      {slaveTools.map(sv => (
                        <SelectItem key={sv.machine_sn || sv.serial_number || sv.id} value={sv.machine_sn || sv.serial_number || sv.sn} className="text-white hover:bg-white/10">
                          {sv.label || sv.tool_name || sv.machine_sn || 'SLAVE'} — {sv.machine_sn || sv.serial_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="mb-4">
                  <Label className="text-zinc-300 text-sm mb-1 block">Tipo de memoria <span className="text-red-400">*</span></Label>
                  <p className="text-zinc-500 text-xs mb-2">Debe coincidir con la memoria del archivo. Se detecta automáticamente por el nombre, pero puedes cambiarlo.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="ticket-memory-type">
                    {[
                      { id: 'int_flash', label: 'Internal Flash' },
                      { id: 'ext_flash', label: 'External Flash' },
                      { id: 'int_eeprom', label: 'Internal EEPROM' },
                      { id: 'ext_eeprom', label: 'External EEPROM' },
                    ].map(m => (
                      <button key={m.id} type="button" onClick={() => setMemoryType(m.id)}
                        data-testid={`ticket-memory-${m.id}`}
                        className={`px-2 py-2 rounded border text-xs font-medium transition-colors ${
                          memoryType === m.id ? 'border-primary bg-primary/15 text-primary' : 'border-white/10 bg-surface-highlight text-zinc-400 hover:text-white'
                        }`}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div
              className={`border-2 border-dashed rounded-md p-8 text-center transition-all ${
                dragActive ? 'border-primary bg-primary/5' : file ? 'border-green-500/30 bg-green-500/5' : 'border-white/20 hover:border-white/30'
              }`}
              onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
              data-testid="dropzone"
            >
              {file ? (
                <div className="space-y-3">
                  <FileType className="w-10 h-10 text-primary mx-auto" />
                  <p className="text-white font-medium">{file.name}</p>
                  <p className="text-zinc-500 text-sm">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  {fileSource === 'slave' && decrypting && (
                    <div className="flex items-center justify-center gap-2 text-blue-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">
                        Desencriptando con FLEX... {decryptedFile?.status}
                        {queueInfo && queueInfo.queue_length > 0 && (
                          <span className="ml-1 text-orange-400">(Cola: {queueInfo.queue_length} en espera)</span>
                        )}
                      </span>
                    </div>
                  )}
                  {fileSource === 'slave' && decryptedFile && !decrypting && decryptedFile.status !== 'FAILED' && (
                    <p className="text-green-400 text-sm flex items-center justify-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Desencriptado: {decryptedFile.filename || 'OK'}
                    </p>
                  )}
                  {fileSource === 'slave' && decryptedFile?.status === 'FAILED' && (
                    <p className="text-red-400 text-sm">Error al desencriptar</p>
                  )}
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setFile(null); setDecryptedFile(null); setFileAnalysis(null); setDecrypting(false); }}
                    className="text-zinc-400 hover:text-white">
                    <X className="w-4 h-4 mr-1" /> Cambiar
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className="w-10 h-10 text-zinc-600 mx-auto" />
                  <p className="text-white">{t('Arrastra tu archivo aquí')} ({fileSource === 'slave' ? t('encriptado') : '.bin'})</p>
                  <p className="text-zinc-500 text-sm">o haz clic para seleccionar</p>
                  <input type="file" onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
                    className="hidden" id="file-input" accept={fileSource === 'slave' ? '.bin,.hex,.ori,.mmf,.mmf2,.slv' : '.bin,.hex,.ori'} />
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => document.getElementById('file-input').click()}
                    className="border-white/20 text-white hover:bg-white/10">
                    Seleccionar archivo
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* File Analysis Results */}
          {analyzing && (
            <div className="bg-surface border border-blue-500/30 rounded-md p-6 flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
              <span className="text-blue-400">Analizando firmware...</span>
            </div>
          )}
          {fileAnalysis && !analyzing && (
            <div className={`bg-surface border rounded-md p-6 ${fileAnalysis.match_found ? 'border-green-500/40' : 'border-yellow-500/30'}`}>
              <div className="flex items-center gap-2 mb-3">
                {fileAnalysis.match_found ? (
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                )}
                <div>
                  <h3 className={`font-heading font-bold ${fileAnalysis.match_found ? 'text-green-400' : 'text-yellow-400'}`}>
                    {fileAnalysis.match_found ? t('¡SOLUCIÓN AUTOMÁTICA DISPONIBLE!') : t('ANÁLISIS DE FIRMWARE')}
                  </h3>
                  <p className="text-zinc-400 text-sm">{fileAnalysis.message}</p>
                </div>
                {fileAnalysis.confidence && (
                  <span className="ml-auto text-xs font-mono px-2 py-1 rounded bg-white/5 text-zinc-300">
                    {Math.round(fileAnalysis.confidence * 100)}% confianza
                  </span>
                )}
              </div>

              {/* Firmware info */}
              {(fileAnalysis.firmware_info || fileAnalysis.analysis) && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                  {[
                    {label: 'ECU', value: fileAnalysis.analysis?.ecu_family_hint?.value || fileAnalysis.firmware_info?.ecu_family},
                    {label: 'HW', value: fileAnalysis.analysis?.hw_id?.value || fileAnalysis.firmware_info?.hw_id},
                    {label: 'SW', value: fileAnalysis.analysis?.sw_id?.value || fileAnalysis.firmware_info?.sw_id},
                    {label: 'OEM', value: fileAnalysis.analysis?.oem_hint?.value || fileAnalysis.firmware_info?.oem},
                  ].filter(i => i.value).map((item, idx) => (
                    <div key={idx} className="bg-surface-highlight rounded px-3 py-2">
                      <span className="text-zinc-500 text-xs block">{item.label}</span>
                      <span className="text-white text-sm font-mono">{item.value}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Match details + Quick Buy */}
              {/* Weak matches: pending manual review, not purchasable */}
              {fileAnalysis.weak_matches && fileAnalysis.possible_matches?.length > 0 && (
                <div className="mt-4 bg-yellow-500/5 border border-yellow-500/20 rounded-md p-4" data-testid="possible-matches">
                  <p className="text-yellow-400 text-sm font-medium mb-2">{t('Posibles coincidencias (pendientes de revisión manual)')}</p>
                  <div className="space-y-1">
                    {fileAnalysis.possible_matches.map((m, i) => (
                      <p key={i} className="text-zinc-400 text-xs">
                        {m.vehicle} · {m.ecu_type} · {Math.round(m.confidence * 100)}%
                      </p>
                    ))}
                  </div>
                  <p className="text-zinc-500 text-xs mt-2">{t('Envía la solicitud y nuestro equipo verificará la compatibilidad.')}</p>
                </div>
              )}

              {fileAnalysis.match_found && !quickBuyResult && (
                <div className="bg-green-500/5 border border-green-500/20 rounded p-4">
                  <p className="text-white text-sm mb-2">
                    <strong>Vehículo:</strong> {fileAnalysis.vehicle} · <strong>ECU:</strong> {fileAnalysis.ecu_type}
                    {fileAnalysis.engine_code && <> · <strong>Motor:</strong> {fileAnalysis.engine_code}</>}
                    {fileAnalysis.horsepower && <> · <strong>CV:</strong> {fileAnalysis.horsepower}</>}
                  </p>
                  <p className="text-zinc-400 text-xs mb-3">Tipo de coincidencia: {fileAnalysis.match_type}</p>

                  <div className="space-y-2">
                    {fileAnalysis.available_solutions?.map((sol, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-surface-highlight rounded px-3 py-2.5">
                        <div className="flex-1">
                          <span className="text-white text-sm">{sol.solution_names?.join(', ') || sol.solutions?.join(', ')}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-primary font-mono font-bold">{sol.price}€</span>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleQuickBuy(sol.id)}
                            disabled={quickBuying || (!user?.is_admin && (user?.credits || 0) < sol.price)}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs"
                            data-testid={`quick-buy-btn-${idx}`}
                          >
                            {quickBuying ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <ShoppingCart className="w-3 h-3 mr-1" />
                            )}
                            {user?.is_admin ? 'Descargar' : 'Compra Rápida'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 pt-3 border-t border-green-500/20 flex items-center justify-between">
                    {user?.is_admin ? (
                      <span className="text-green-400 text-xs font-medium">ADMIN - Sin coste</span>
                    ) : (
                      <span className="text-zinc-400 text-xs">Tu saldo: <strong className="text-white">{user?.credits || 0} créditos</strong></span>
                    )}
                    <span className="text-zinc-500 text-xs">O envía solicitud al admin abajo</span>
                  </div>
                </div>
              )}

              {/* Quick Buy Success */}
              {quickBuyResult && (
                <div className="bg-green-500/10 border border-green-500/30 rounded p-5 text-center">
                  <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
                  <h3 className="font-heading text-green-400 font-bold text-lg mb-1">{t('COMPRA COMPLETADA')}</h3>
                  <p className="text-zinc-400 text-sm mb-1">{quickBuyResult.message}</p>
                  <p className="text-zinc-500 text-xs mb-4">Ticket: {quickBuyResult.ticket_number} (auto-cerrado)</p>
                  <div className="flex gap-3 justify-center">
                    <Button
                      type="button"
                      onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/downloads/${quickBuyResult.download_token}`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(res.data);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = quickBuyResult.filename || 'archivo';
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch {
                          toast.error('Error al descargar. El enlace puede haber expirado.');
                        }
                      }}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="quick-buy-download-btn"
                    >
                      <Download className="w-4 h-4 mr-2" /> {t('Descargar archivo')}
                    </Button>
                    <Button type="button" variant="outline" className="border-white/10 text-white"
                      onClick={() => { setFile(null); setFileAnalysis(null); setQuickBuyResult(null); setDecryptedFile(null); }}>
                      {t('Nueva solicitud')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Analysis summary */}
              {fileAnalysis.analysis?.summary && !fileAnalysis.match_found && (
                <p className="text-zinc-400 text-sm mt-2 italic">{fileAnalysis.analysis.summary}</p>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="bg-surface border border-white/10 rounded-md p-6">
            <Label className="text-zinc-300 mb-2 block">{t('Notas / DTCs a eliminar (opcional)')}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder={t('DTCs, observaciones, información adicional...')}
              className="bg-surface-highlight border-white/10 text-white min-h-[80px]"
              data-testid="notes-textarea" />
          </div>

          {/* Submit */}
          <Button type="submit"
            disabled={loading || !brand || !model || !ecuType || !licensePlate || !transmission || !readingTool || selectedServices.length === 0 || !file || (selectedServices.some(id => EMISSION_SERVICES.includes(id)) && !disclaimerAccepted)}
            className="w-full bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wider h-14 text-base"
            data-testid="submit-request-btn">
            {loading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t('Enviando solicitud...')}</>
            ) : (
              <><Upload className="w-5 h-5 mr-2" /> {t('Enviar Solicitud')}</>
            )}
          </Button>
        </form>
      </div>
    </Layout>
  );
};

export default NewRequestPage;
